import { Component, computed, DestroyRef, effect, inject, output, signal } from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { MatButtonModule } from '@angular/material/button';
import { MatCardModule } from '@angular/material/card';
import { MatDividerModule } from '@angular/material/divider';
import { MatProgressBar } from '@angular/material/progress-bar';
import { MatProgressSpinnerModule } from '@angular/material/progress-spinner';
import { MatSnackBar } from '@angular/material/snack-bar';

import { ToolImportStatusService } from '@tfs/tool-import-status';

import { UserOnboardingProfileDataStore } from '../../data-access/user-onboarding-profile.data-store';

type DataPreference = 'empty' | 'demo' | null;

@Component({
  selector: 'tfs-user-onboarding-data',
  imports: [
    MatButtonModule,
    MatDividerModule,
    MatCardModule,
    MatProgressBar,
    MatProgressSpinnerModule,
  ],
  templateUrl: './user-onboarding-data.component.html',
  styleUrl: './user-onboarding-data.component.scss',
})
export class UserOnboardingDataComponent {
  private readonly toolImportStatusService = inject(ToolImportStatusService);
  private readonly profileDataStore = inject(UserOnboardingProfileDataStore);
  private readonly snackBar = inject(MatSnackBar);
  private readonly destroyRef = inject(DestroyRef);

  readonly next = output();

  protected readonly selectedPreference = signal<DataPreference>(null);

  protected readonly isSaving = computed(
    () => this.toolImportStatusService.isLoading() || this.profileDataStore.isLoading()
  );

  protected readonly isDemoImportRunning = this.toolImportStatusService.isImportRunning;

  protected readonly canSubmit = computed(
    () => this.selectedPreference() !== null && !this.isSaving() && !this.isDemoImportRunning()
  );

  constructor() {
    effect(() => {
      if (this.isDemoImportRunning()) {
        this.selectedPreference.set('demo');
      }
    });
  }

  protected selectPreference(preference: DataPreference): void {
    this.selectedPreference.set(preference);
  }

  protected onNextClick(): void {
    if (this.selectedPreference() === 'demo') {
      this.startDemoImport();
    } else {
      this.next.emit();
    }
  }

  private startDemoImport(): void {
    this.toolImportStatusService
      .startDemoDataImport()
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe({
        next: importSucceeded => {
          if (importSucceeded) {
            this.snackBar.open('Demo data has successfully been imported.', 'OK', {
              duration: 4000,
              panelClass: 'success',
            });
            this.next.emit();
          } else {
            this.snackBar.open('Failed to import demo data.', 'OK', {
              duration: 4000,
              panelClass: 'error',
            });
          }
        },
      });
  }
}
